export type PartialPaymentValue = {
  enabled: boolean;
};

export const PARTIAL_PAYMENT_CONFLICT_MESSAGES = {
  PARTIAL_AUTHORIZATIONS:
    'Partial Authorization cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  SPLIT_PAYMENTS:
    'Split Payments cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  PARTIAL_PAYMENTS:
    'Allow Partial Payments cannot be enabled while Partial Authorization or Split Payments is on. Turn those options off first.',
} as const;

export function isPartialPaymentEnabled(config?: { value?: PartialPaymentValue } | null): boolean {
  return config?.value?.enabled === true;
}

/** PARTIAL_AUTHORIZATIONS stores a boolean in `value`; treat missing value as disabled. */
export function isPartialAuthorizationEnabled(config?: { value?: boolean | null } | null): boolean {
  return config?.value === true;
}

export function isPartialPaymentBlockedByPartialAuth(partialAuthEnabled: boolean): boolean {
  return partialAuthEnabled;
}

export function isPartialPaymentBlockedBySplitPayments(splitPaymentsEnabled: boolean): boolean {
  return splitPaymentsEnabled;
}

export function getPartialPaymentToggleDisabledReason(
  partialAuthEnabled: boolean,
  splitPaymentsEnabled: boolean,
): string | null {
  if (partialAuthEnabled && splitPaymentsEnabled) {
    return PARTIAL_PAYMENT_CONFLICT_MESSAGES.PARTIAL_PAYMENTS;
  }
  if (partialAuthEnabled) {
    return 'Allow Partial Payments cannot be enabled while Partial Authorization is on. Turn off Partial Authorization first.';
  }
  if (splitPaymentsEnabled) {
    return 'Allow Partial Payments cannot be enabled while Split Payments is on. Turn off Split Payments first.';
  }
  return null;
}

export function getPartialAuthToggleDisabledReason(partialPaymentEnabled: boolean): string | null {
  if (!partialPaymentEnabled) {
    return null;
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES.PARTIAL_AUTHORIZATIONS;
}

export function getSplitPaymentsToggleDisabledReason(
  partialPaymentEnabled: boolean,
): string | null {
  if (!partialPaymentEnabled) {
    return null;
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES.SPLIT_PAYMENTS;
}

type ConfigUpsertLike = {
  name: string;
  value?: unknown;
  enabled?: boolean;
};

/** When saving multiple payment configs at once, apply disables before enables to satisfy MMS mutual exclusion. */
export function orderConfigsForPartialPaymentMutualExclusion<T extends ConfigUpsertLike>(
  configs: T[],
): T[] {
  const tier = (config: ConfigUpsertLike): number => {
    if (config.name === 'PARTIAL_PAYMENTS') {
      const enabled = (config.value as PartialPaymentValue | undefined)?.enabled;
      if (enabled === false) {
        return 0;
      }
      if (enabled === true) {
        return 2;
      }
    }
    if (config.name === 'PARTIAL_AUTHORIZATIONS') {
      if (config.value === false) {
        return 0;
      }
      if (config.value === true) {
        return 2;
      }
    }
    if (config.name === 'SPLIT_PAYMENTS') {
      if (config.enabled === false) {
        return 0;
      }
      if (config.enabled === true) {
        return 2;
      }
    }
    return 1;
  };

  return [...configs].sort((a, b) => tier(a) - tier(b));
}
